/**
 * IcgAuthorityExplainer — explainer for ICG-Dt, the AUTHORITY Immunity
 * Closure Guarantee (governance name: AUTHORITY Safety Envelope).
 *
 * ICG-Dt pairs the PERMIT Neutralization Theorem (primal) with the
 * DELEGATE Structural Duality Principle (dual) along the slice ⊣ coslice
 * adjoint triple Σ_f ⊣ f* ⊣ Π_f. The shared base-change functor f* forces
 * authority use and authority grant to agree: no ambient position grants
 * authority without a typed witness.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AuditSurfaceDelta,
  CalloutBox,
  CounterModelGrid,
  ExplainerCard,
  SafetyEnvelopeDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const PRIMAL_ACCENT = THEOREM_COLOR.PERMIT;
const DUAL_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.DELEGATE.color;

const COUNTER_MODELS = [
  {
    title: "Primal only — PERMIT, no DELEGATE",
    body: "Acting without authority is unconstructable inside one regime, but stacking a delegation chain is unwitnessed: nothing certifies that delegated authority stays within what is held as the chain composes, so a downstream component can be induced to act on authority it never received.",
  },
  {
    title: "Dual only — DELEGATE, no PERMIT",
    body: "The slice ⊣ coslice duality composes grants, but with no permit neutralization there is no use-side guarantee to compose — DELEGATE certifies the chaining of an authority that was never gated.",
  },
] as const;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-icg-dt",
  kind: "SSM · Immunity Closure Guarantee (S1⊗S2)",
  title: "ICG-Dt",
  subtitle: "AUTHORITY Safety Envelope · PERMIT ⊗ DELEGATE",
  accent: PRIMAL_ACCENT,
  accentAlt: DUAL_ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "PERMIT (primal NT)", href: "#explainer-permit", accent: PRIMAL_ACCENT },
  { label: "DELEGATE (dual SDP)", href: "#explainer-delegate", accent: DUAL_ACCENT },
  { label: "Slice / Coslice", href: "#explainer-slice-category" },
];

const ENVELOPE = (
  <SafetyEnvelopeDiagram
    icg="ICG-Dt"
    primalLabel="PERMIT"
    primalKind="Neutralization Theorem · P"
    primalAccent={PRIMAL_ACCENT}
    dualLabel="DELEGATE"
    dualKind="Structural Duality Principle · Dt"
    dualAccent={DUAL_ACCENT}
    adjunction="slice ⊣ coslice (Σ_f ⊣ f* ⊣ Π_f)"
    unitRole="authority use"
    counitRole="authority grant"
  />
);

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ICG-Dt</strong> is the AUTHORITY Safety Envelope. It seals authority neutralization
      (PERMIT) and its compositional dual (DELEGATE) into one two-sided certificate, so use and
      grant cannot drift apart.
    </p>
  ),
  intuition: (
    <>
      <p>
        PERMIT makes acting without authority unconstructable: an effect is an object over the
        authority it consumes. DELEGATE certifies that the slice ↔ coslice duality preserves that
        guarantee as authority is delegated.
      </p>
      <p>
        Held separately, the use-side and the grant-side are sound about different things: the
        permit check certifies callees, the delegate check certifies callers, and the coherence
        binding them is a paragraph. A confused-deputy attack lives in that paragraph.
      </p>
      <p>
        ICG-Dt is the single adjoint triple that contains both sides on the same base. Its unit and
        counit are the structural proof that authority use and authority grant are complementary —
        the envelope is <strong>strictly stronger</strong> than holding the two apart.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ICG-Dt is the adjoint triple{" "}
        <MathInline>{tex`\Sigma_{f} \dashv f^{*} \dashv \Pi_{f}`}</MathInline> — two interlocking
        adjunctions sharing the base-change spine <MathInline>{tex`f^{*}`}</MathInline>. Its
        structural witnesses are:
      </p>
      <ul>
        <li>
          the <strong>unit η</strong> of <MathInline>{tex`\Sigma_{f} \dashv f^{*}`}</MathInline> —
          authority <em>use</em>: an effect presented over the authority it consumes;
        </li>
        <li>
          the <strong>counit ε</strong> of <MathInline>{tex`f^{*} \dashv \Pi_{f}`}</MathInline> —
          authority <em>grant</em>: a delegation strictly narrower than what is held.
        </li>
      </ul>
      <p>
        The shared spine <MathInline>{tex`f^{*}`}</MathInline> forces use and grant to agree on a
        typed witness: there is no ambient slot from which authority arises unattributed, and
        delegated authority cannot exceed what is held.
      </p>
      <AuditSurfaceDelta
        primalCheck="PERMIT witness"
        dualCheck="DELEGATE witness"
        envelopeLabel="ICG-Dt envelope"
        primalAccent={PRIMAL_ACCENT}
        dualAccent={DUAL_ACCENT}
      />
      <p style={{ marginTop: "0.5rem" }}>
        Categorical form: ICG-Dt is the <em>Adjunction Certificate</em> whose two component
        adjunctions share <MathInline>{tex`f^{*}`}</MathInline>; the unit and counit are the
        certificate that the slice side and the coslice side describe the same authority object.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Each side alone admits a counter-model the
        envelope rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="ICG-Dt counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        ICG-Dt makes the forbidden generator <strong>G1 (ambient authority)</strong> structurally
        absent: the adjoint triple has no ambient position — delegating authority you do not hold is
        unconstructable. The envelope enforces security axiom A1, No Ambient Authority.
      </p>
      <p>
        <strong>Worked adversary.</strong> The classic confused deputy: a component is induced to
        delegate (or exercise on behalf of) authority broader than its caller actually holds. The
        permit check sees a valid callee, the delegate check sees a valid grant chain, but neither
        certifies that the chain stays within what was held. Under ICG-Dt the base-change spine ties
        use and grant to one authority object, so the over-delegation does not type.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing ICG-Dt means bypassing PERMIT and
        DELEGATE at once, or escaping the slice/coslice carriers — both leave the kernel category
        and vacate the guarantee rather than violate it.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-Dt" accent={DUAL_ACCENT}>
        PERMIT (NT) ⊗ DELEGATE (SDP) = ICG-Dt. The audit surface collapses from an NT witness plus
        an SDP witness to one envelope carrying the adjoint-triple unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Immunity Closure Guarantees (§7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "RD-97 — Immunity Closure Guarantees",
      path: "research/settled/RD-97-BI-GUARANTEES-TWO-SIDED-SAFETY.md",
      note: "Canonical source for the seven-envelope algebra and the naming triad.",
    },
    {
      label: "Safety Envelope foundations",
      path: "theory/structural/Immunity-Closure-Guarantees/00-Safety-Envelope-Foundations.md",
    },
    {
      label: "ICG-Dt theory document",
      path: "theory/structural/Immunity-Closure-Guarantees/05-ICG-Dt-Permit-And-Delegate.md",
    },
    {
      label: "Immunity Closure Guarantee (Agda)",
      path: "agda/Research/BiGuarantee/BiAuthority.agda",
    },
  ],
};

export function IcgAuthorityExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={ENVELOPE} crossLinks={CROSS_LINKS} />
  );
}
